import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  ArrowRight,
  LoaderCircle,
  X,
  Leaf,
  Image as ImageIcon,
} from "lucide-react";
import { useI18n, PRODUCT_SHORT_NAME } from "./i18n";
import { privateBlob } from "./api";
export function Button({
  children,
  kind = "",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { kind?: string }) {
  return (
    <button className={`button ${kind}`} {...props}>
      {children}
    </button>
  );
}
export function Brand({ compact = false }: { compact?: boolean }) {
  const { t } = useI18n();
  return (
    <span className="brand">
      <span className="brand-mark">
        <Leaf size={23} />
      </span>
      {!compact && (
        <span>
          {PRODUCT_SHORT_NAME}
          <small>{t("FAMILY WORKSPACES", "מרחבים משפחתיים")}</small>
        </span>
      )}
    </span>
  );
}
export function Loading() {
  const { t } = useI18n();
  return (
    <div className="loading" role="status">
      <LoaderCircle className="spin" />
      {t("Gathering your memories…", "טוענים את הזיכרונות שלכם…")}
    </div>
  );
}
export function ErrorBox({
  error,
  retry,
}: {
  error: string;
  retry?: () => void;
}) {
  const { t } = useI18n();
  return error ? (
    <div className="error-box" role="alert">
      {error}
      {retry && <button onClick={retry}>{t("Try again", "נסו שוב")}</button>}
    </div>
  ) : null;
}
export function Empty({
  icon: Icon = Leaf,
  title,
  description,
  action,
}: {
  icon?: typeof Leaf;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <section className="empty-state">
      <div className="empty-icon">
        <Icon size={34} strokeWidth={1} />
      </div>
      <h2>{title}</h2>
      <p>{description}</p>
      {action}
    </section>
  );
}
export function Modal({
  title,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const { t } = useI18n();
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.showModal();
    return () => {
      ref.current?.close();
      previous?.focus();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className={wide ? "modal wide" : "modal"}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
    >
      <header>
        <h2 id={titleId}>{title}</h2>
        <button
          className="icon-button"
          aria-label={t("Close", "סגירה")}
          onClick={onClose}
        >
          <X />
        </button>
      </header>
      {children}
    </dialog>
  );
}
export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}
export function Heading({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="page-heading">
      <div>
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {action}
    </div>
  );
}
export function PrivateImage({
  path,
  alt,
  className = "",
  ...props
}: {
  path?: string;
  alt: string;
  className?: string;
} & React.ImgHTMLAttributes<HTMLImageElement>) {
  const [url, setUrl] = useState("");
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    let objectUrl = "";
    setUrl("");
    setFailed(false);
    if (path)
      privateBlob(path)
        .then((blob) => {
          objectUrl = URL.createObjectURL(blob);
          if (active) setUrl(objectUrl);
          else URL.revokeObjectURL(objectUrl);
        })
        .catch(() => {
          if (active) setFailed(true);
        });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);
  return url ? (
    <img src={url} alt={alt} className={className} {...props} />
  ) : (
    <span
      className={`image-placeholder ${className}`}
      role="img"
      aria-label={alt}
    >
      <ImageIcon size={30} />
      {failed && <small>—</small>}
    </span>
  );
}
export function Arrow() {
  return <ArrowRight size={17} className="direction-arrow" />;
}
export function DateText({ value }: { value?: string }) {
  const { language } = useI18n();
  if (!value) return null;
  const date = new Date(value);
  return (
    <time dateTime={value}>
      {isNaN(date.getTime())
        ? value
        : date.toLocaleDateString(language === "he" ? "he-IL" : "en-CA", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
    </time>
  );
}

export function useEditVersion(initialVersion: number) {
  const [expectedVersion, setVersion] = useState(initialVersion);
  const [conflict, setConflict] = useState<import("./types").RecordData | null>(
    null,
  );
  function capture(error: unknown) {
    const value = error as {
      code?: string;
      details?: { current?: import("./types").RecordData };
    };
    if (value.code === "VERSION_CONFLICT" && value.details?.current)
      setConflict(value.details.current);
  }
  function acceptLatest() {
    if (conflict) setVersion(conflict.version);
    setConflict(null);
  }
  return {
    expectedVersion,
    conflict,
    capture,
    acceptLatest,
    setVersion,
    setConflict,
  };
}
export function ConflictReview({
  current,
  onAccept,
}: {
  current: import("./types").RecordData | null;
  onAccept: () => void;
}) {
  const { t } = useI18n();
  if (!current) return null;
  const fields = [
    ["name", "Name", "שם"],
    ["title", "Title", "כותרת"],
    ["hebrewName", "Hebrew name", "שם בעברית"],
    ["description", "Description", "תיאור"],
    ["biography", "Biography", "ביוגרפיה"],
    ["text", "Text", "טקסט"],
    ["birthDate", "Birth date", "תאריך לידה"],
    ["deathDate", "Death date", "תאריך פטירה"],
    ["dateTaken", "Photograph date", "תאריך התצלום"],
    ["location", "Place", "מקום"],
    ["tags", "Tags", "תגיות"],
  ];
  return (
    <section className="conflict-review" role="alert">
      <h3>{t("Compare before saving", "השוו לפני השמירה")}</h3>
      <p>
        {t(
          "Your draft remains in the form. Review the latest saved details, adjust your draft, then explicitly choose to save against this version.",
          "הטיוטה שלכם נשארת בטופס. בדקו את הפרטים העדכניים שנשמרו, התאימו את הטיוטה ואז בחרו במפורש לשמור מול גרסה זו.",
        )}
      </p>
      <div className="conflict-snapshot">
        <b>
          {t("Latest saved version", "הגרסה האחרונה שנשמרה")} {current.version}
        </b>
        <dl>
          {fields
            .filter(([key]) => current[key] !== undefined)
            .map(([key, en, he]) => (
              <div key={key}>
                <dt>{t(en, he)}</dt>
                <dd dir="auto">
                  {Array.isArray(current[key])
                    ? (current[key] as string[]).join(", ")
                    : String(current[key] || "—")}
                </dd>
              </div>
            ))}
        </dl>
        {Array.isArray(current.blocks) && (
          <div className="story-body">
            {(current.blocks as { id: string; text: string }[]).map((block) => (
              <p key={block.id} dir="auto">
                {block.text}
              </p>
            ))}
          </div>
        )}
      </div>
      <Button type="button" kind="secondary" onClick={onAccept}>
        {t(
          "Keep my draft and use this version for the next save",
          "שמירת הטיוטה והשתמשות בגרסה זו בשמירה הבאה",
        )}
      </Button>
    </section>
  );
}
